/**
 * Entity Type System
 * 
 * Provides type-safe identification of game entities without relying on
 * naming conventions. All entities (characters, objects, projectiles, etc.)
 * register with the EntityRegistry and can be queried by type.
 */

import type { Vector3 } from '@babylonjs/core';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { AnyActorRef } from 'xstate';

/**
 * Entity type enum - identifies what kind of game entity this is
 */
export enum EntityType {
  /** Player or NPC character */
  CHARACTER = 'character',
  /** Dynamic physics object (crates, barrels, etc.) */
  DYNAMIC_OBJECT = 'dynamic_object',
  /** Projectile (bullets, thrown objects) */
  PROJECTILE = 'projectile',
  /** Static level geometry */
  STATIC_GEOMETRY = 'static_geometry',
  /** Trigger zone for events */
  TRIGGER_ZONE = 'trigger_zone',
  /** Debris from destroyed objects */
  DEBRIS = 'debris',
  /** Particle emitter */
  PARTICLE_EMITTER = 'particle_emitter',
  /** Unknown/unregistered entity */
  UNKNOWN = 'unknown'
}

/**
 * Character subtypes for more granular identification
 */
export enum CharacterSubtype {
  /** Player-controlled character */
  PLAYER = 'player',
  /** Friendly NPC (party member, ally) */
  PARTY_MEMBER = 'party_member',
  /** Hostile NPC */
  ENEMY = 'enemy',
  /** Neutral NPC (shopkeeper, civilian) */
  NEUTRAL = 'neutral'
}

/**
 * Information about an entity in the game world
 */
export interface EntityInfo {
  /** Unique entity ID */
  id: string;
  
  /** Physics body ID (may differ from entity ID) */
  physicsBodyId: string;
  
  /** Type of entity */
  type: EntityType;
  
  /** Behavior ID for BehaviorRegistry lookup */
  behaviorId?: string;
  
  /** Character type (e.g., 'daniel', 'lilly', 'hotdog') */
  characterType?: string;
  
  /** Character subtype for characters */
  characterSubtype?: CharacterSubtype;
  
  /** Whether this is the player-controlled character */
  isPlayer: boolean;
  
  /** XState actor for state management (if applicable) */
  actor?: AnyActorRef;
  
  /** Primary mesh for this entity */
  mesh?: AbstractMesh;
  
  /** Display name for debugging */
  displayName?: string;
  
  /** Custom metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Options for particle spawning
 */
export interface ParticleSpawnOptions {
  /** World position to spawn at (defaults to contact point) */
  position?: Vector3;
  /** Direction particles should emit (defaults to contact normal) */
  direction?: Vector3;
  /** Number of particles (defaults to preset default) */
  count?: number;
  /** Scale multiplier for particles */
  scale?: number;
  /** Color tint [r, g, b, a] */
  color?: [number, number, number, number];
  /** Velocity multiplier */
  velocityScale?: number;
}

/**
 * Options for sound playback
 */
export interface SoundPlayOptions {
  /** Volume multiplier (0-1) */
  volume?: number;
  /** Pitch multiplier */
  pitch?: number;
  /** World position for spatial audio */
  position?: Vector3;
  /** Whether to loop the sound */
  loop?: boolean;
}

/**
 * Rich context for collision events
 */
export interface CollisionContext {
  /** The entity receiving the collision (self) */
  self: EntityInfo;
  
  /** The other entity in the collision */
  other: EntityInfo;
  
  /** Impact speed in m/s (relative velocity magnitude) */
  impulse: number;
  
  /** World position of contact point */
  contactPoint: Vector3;
  
  /** Surface normal at contact point */
  contactNormal: Vector3;
  
  /** Relative velocity vector */
  relativeVelocity: Vector3;
}

/**
 * Actions available during collision handling
 */
export interface CollisionActions {
  // === Damage ===
  
  /** Apply damage to self */
  applyDamage(amount: number): void;
  
  /** Apply damage to the other entity */
  applyDamageToOther(amount: number): void;
  
  // === Physics ===
  
  /** Apply impulse force to self */
  applyForce(direction: Vector3, magnitude: number): void;
  
  /** Apply impulse force to the other entity */
  applyForceToOther(direction: Vector3, magnitude: number): void;
  
  // === Effects ===
  
  /** Spawn particles using a preset */
  spawnParticles(preset: string, options?: ParticleSpawnOptions): void;
  
  /** Play a sound effect */
  playSound(soundId: string, options?: SoundPlayOptions): void;
  
  // === State ===
  
  /** Immediately destroy this entity */
  destroy(): void;
  
  /** Send an event to this entity's state machine */
  sendEvent(eventType: string, payload?: Record<string, unknown>): void;
  
  /** Get current health of this entity */
  getHealth(): number;
  
  /** Get current state name of this entity */
  getState(): string;
  
  /** Get the XState actor for this entity */
  getActor(): AnyActorRef | undefined;
  
  // === Other Entity ===
  
  /** Send an event to the other entity's state machine */
  sendEventToOther(eventType: string, payload?: Record<string, unknown>): void;
  
  /** Get current health of the other entity */
  getOtherHealth(): number;
  
  /** Get current state of the other entity */
  getOtherState(): string;
}

/**
 * Game services available to hooks
 */
export interface GameServices {
  /** Access to physics system */
  physics: {
    applyImpulse(bodyId: string, impulse: Vector3): void;
    getVelocity(bodyId: string): Vector3 | null;
  };
  
  /** Access to particle system */
  particles: {
    spawn(preset: string, position: Vector3, options?: ParticleSpawnOptions): void;
  };
  
  /** Access to audio system */
  audio: {
    play(soundId: string, options?: SoundPlayOptions): void;
  };
  
  /** Access to state management */
  state: {
    getActor(entityId: string): AnyActorRef | undefined;
    sendEvent(entityId: string, event: { type: string; [key: string]: unknown }): void;
  };
}

/**
 * Full API provided to collision hooks
 */
export interface CollisionHookAPI {
  /** Collision context with entity info */
  ctx: CollisionContext;
  
  /** Actions to perform */
  actions: CollisionActions;
  
  /** Access to game services */
  services: GameServices;
}

/**
 * Full API provided to damage hooks
 */
export interface DamageHookAPI {
  /** Entity receiving damage */
  self: EntityInfo;
  
  /** Source of damage (if known) */
  source?: EntityInfo;
  
  /** Damage amount */
  amount: number;
  
  /** Damage type (e.g., 'impact', 'fire', 'explosion') */
  damageType: string;
  
  /** Impact point (if applicable) */
  impactPoint?: Vector3;
  
  /** Actions available */
  actions: {
    /** Modify the damage amount (return new value) */
    modifyDamage(newAmount: number): void;
    /** Cancel the damage entirely */
    cancelDamage(): void;
    /** Spawn particles */
    spawnParticles(preset: string, options?: ParticleSpawnOptions): void;
    /** Play sound */
    playSound(soundId: string, options?: SoundPlayOptions): void;
    /** Get current health */
    getHealth(): number;
    /** Get current state */
    getState(): string;
  };
  
  /** Game services */
  services: GameServices;
}

/**
 * Full API provided to spawn hooks
 */
export interface SpawnHookAPI {
  /** Entity being spawned */
  self: EntityInfo;
  
  /** Spawn configuration */
  config: Record<string, unknown>;
  
  /** Spawn position */
  position: Vector3;
  
  /** Actions available */
  actions: {
    /** Spawn particles at this location */
    spawnParticles(preset: string, options?: ParticleSpawnOptions): void;
    /** Play sound */
    playSound(soundId: string, options?: SoundPlayOptions): void;
    /** Set initial state */
    sendEvent(eventType: string, payload?: Record<string, unknown>): void;
    /** Get the actor */
    getActor(): AnyActorRef | undefined;
  };
  
  /** Game services */
  services: GameServices;
}

/**
 * Full API provided to destroy hooks
 */
export interface DestroyHookAPI {
  /** Entity being destroyed */
  self: EntityInfo;
  
  /** Position at destruction */
  position: Vector3;
  
  /** What caused destruction (if known) */
  cause?: 'damage' | 'timeout' | 'manual' | 'unknown';
  
  /** Actions available */
  actions: {
    /** Spawn particles */
    spawnParticles(preset: string, options?: ParticleSpawnOptions): void;
    /** Play sound */
    playSound(soundId: string, options?: SoundPlayOptions): void;
    /** Spawn a new entity (e.g., loot drop) */
    spawnEntity(assetType: string, position: Vector3, options?: Record<string, unknown>): void;
  };
  
  /** Game services */
  services: GameServices;
}

/**
 * Full API provided to break hooks (when object starts breaking)
 */
export interface BreakHookAPI {
  /** Entity that is breaking */
  self: EntityInfo;
  
  /** Position where break occurred */
  position: Vector3;
  
  /** Actions available */
  actions: {
    /** Spawn particles at this location */
    spawnParticles(preset: string, options?: ParticleSpawnOptions): void;
    /** Play sound */
    playSound(soundId: string, options?: SoundPlayOptions): void;
    /** Trigger screen impact effect (ripple + shake). Preset: 'small' | 'medium' | 'large' */
    triggerImpactEffect(preset: 'small' | 'medium' | 'large', intensity?: number): void;
  };
  
  /** Game services */
  services: GameServices;
}

/**
 * Type guard helpers
 */
export const EntityTypeHelpers = {
  /** Check if entity is a character */
  isCharacter: (info: EntityInfo): boolean => info.type === EntityType.CHARACTER,
  
  /** Check if entity is the player */
  isPlayer: (info: EntityInfo): boolean => info.isPlayer,
  
  /** Check if entity is a player-controlled character */
  isPlayerCharacter: (info: EntityInfo): boolean => 
    info.type === EntityType.CHARACTER && info.isPlayer,
  
  /** Check if entity is an NPC */
  isNPC: (info: EntityInfo): boolean => 
    info.type === EntityType.CHARACTER && !info.isPlayer,
  
  /** Check if entity is an enemy */
  isEnemy: (info: EntityInfo): boolean => 
    info.type === EntityType.CHARACTER && info.characterSubtype === CharacterSubtype.ENEMY,
  
  /** Check if entity is a party member */
  isPartyMember: (info: EntityInfo): boolean =>
    info.type === EntityType.CHARACTER && info.characterSubtype === CharacterSubtype.PARTY_MEMBER,
  
  /** Check if entity is a dynamic object */
  isDynamicObject: (info: EntityInfo): boolean => info.type === EntityType.DYNAMIC_OBJECT,
  
  /** Check if entity is a projectile */
  isProjectile: (info: EntityInfo): boolean => info.type === EntityType.PROJECTILE,
  
  /** Check if entity is debris */
  isDebris: (info: EntityInfo): boolean => info.type === EntityType.DEBRIS,
  
  /** Check if entity is static geometry */
  isStaticGeometry: (info: EntityInfo): boolean => info.type === EntityType.STATIC_GEOMETRY,
  
  /** Check if entity is damageable (has health) */
  isDamageable: (info: EntityInfo): boolean => 
    info.type === EntityType.CHARACTER || info.type === EntityType.DYNAMIC_OBJECT,
};
